import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import DataTable, { Column } from "@/components/tables/DataTable";
import TableHead from "@/components/tables/TableHead";
import StatusBadge from "@/components/badges/StatusBadge";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import OwnershipSelectCheckbox from "@/components/buttons/OwnershipSelectCheckbox";
import { EnvelopeTableSkeletonDesktop, EnvelopeTableSkeletonMobile, ListPageHeaderSkeleton } from "@/components/skeletons";
import { useFetchListApi } from "@/utils/hooks/useFetchListApi";
import {
    buildTransferOwnershipPayload,
    useOwnershipSelection,
} from "@/utils/hooks/useOwnershipSelection";
import { getTemplateById, getTemplateEnvelopeListPage } from "@/services/template.service";
import { transferBulkEnvelopeOwnership, transferEnvelopeOwnership } from "@/services/envelope.service";
import { invalidateOwnershipTransferListCaches } from "@/utils/listPageCache";
import { HEADINGS, LABELS, TABLE_HEADERS } from "@/utils/constants/appConstants/commonConstants";
import { PAGE_LENGTH, StatusOptions, TO_TEXT } from "@/utils/constants/constants";
import { DateFormatter, TimeFormatter, concatenateName } from "@/utils/functions/helperFunctions";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";
import {
    BULK_TRANSFER_OWNERSHIP_QUEUED,
    BULK_TRANSFER_OWNERSHIP_SUCCESS,
    TRANSFER_OWNERSHIP_SUCCESS,
} from "@/utils/constants/messages";

const getRecipientsText = (envelope: any) => {
    if (!envelope.recipients?.length) return "No Recipients Specified";
    return envelope.recipients
        .map((recipient: any) => recipient.full_name)
        .slice(0, 3)
        .join(", ");
};

const TemplateEnvelopeList = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();
    const selection = useOwnershipSelection();
    const [templateLoading, setTemplateLoading] = useState(true);
    const [templateName, setTemplateName] = useState("");
    const [filter, setFilter] = useState<Record<string, any>>({});
    const showTransferActions = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalTransferEnvelopeOwnership);
    const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
    const [bulkTransferLoading, setBulkTransferLoading] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedEnvelope, setSelectedEnvelope] = useState<any>(null);
    const [transferLoading, setTransferLoading] = useState(false);

    const fetchTemplateEnvelopes = useCallback(
        (options: Parameters<typeof getTemplateEnvelopeListPage>[1] & { filters?: Record<string, any> }) => {
            if (!id) {
                return Promise.resolve({
                    data: [],
                    pagination: {
                        page: 1,
                        limit: PAGE_LENGTH,
                        total: 0,
                        totalPages: 0,
                        hasNextPage: false,
                    },
                });
            }

            return getTemplateEnvelopeListPage(id, options);
        },
        [id]
    );

    const {
        items: envelopes,
        loading,
        loadingMore,
        pagination,
        hasNextPage,
        observerRef,
        handleSearch,
        handleFilters,
        reset,
        refetch,
        search: searchItem,
    } = useFetchListApi(fetchTemplateEnvelopes, {
        page: 1,
        limit: PAGE_LENGTH,
        sort: "created_at",
        order: "desc",
    });

    const envelopeIds = useMemo(
        () => envelopes.map((envelope: any) => envelope.id).filter(Boolean),
        [envelopes]
    );

    const handleOpenTransferModal = (envelope: any) => {
        setTransferLoading(false);
        setSelectedEnvelope(envelope);
        setShowTransferModal(true);
    };

    const handleCloseTransferModal = () => {
        setShowTransferModal(false);
        setSelectedEnvelope(null);
    };

    const handleTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || !selectedEnvelope) return;

        setTransferLoading(true);
        try {
            const payload = buildTransferOwnershipPayload(selectedUser);
            await transferEnvelopeOwnership(selectedEnvelope.id, payload);
            successToasterMessage(TRANSFER_OWNERSHIP_SUCCESS);
            handleCloseTransferModal();
            selection.deselect(selectedEnvelope.id);
            invalidateOwnershipTransferListCaches("envelope");
            refetch();
        } catch (error) {
            console.log("Error transferring envelope ownership:", error);
            errorToasterMessage(error);
        } finally {
            setTransferLoading(false);
        }
    };

    const handleBulkTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || selection.selectedIds.length === 0) return;

        setBulkTransferLoading(true);
        try {
            const payload = buildTransferOwnershipPayload(selectedUser);
            const result = await transferBulkEnvelopeOwnership({
                envelope_ids: selection.selectedIds,
                ...payload,
            });
            selection.clear();
            setShowBulkTransferModal(false);
            successToasterMessage(
                result?.is_async
                    ? BULK_TRANSFER_OWNERSHIP_QUEUED
                    : BULK_TRANSFER_OWNERSHIP_SUCCESS,
            );
            invalidateOwnershipTransferListCaches("envelope");
            refetch();
        } catch (error) {
            console.log("Error bulk transferring template envelope ownership:", error);
            errorToasterMessage(error);
        } finally {
            setBulkTransferLoading(false);
        }
    };

    const envelopeColumns: Column<any>[] = useMemo(() => {
        const columns: Column<any>[] = [];

        if (showTransferActions) {
            columns.push({
                key: "select",
                label: (
                    <OwnershipSelectCheckbox
                        id="template-envelope-table-select-all"
                        checked={
                            selection.areAllSelected(envelopeIds)
                                ? true
                                : selection.areSomeSelected(envelopeIds)
                                  ? "indeterminate"
                                  : false
                        }
                        onCheckedChange={() => selection.toggleAll(envelopeIds)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label="Select all envelopes"
                    />
                ),
                width: "3rem",
                headerClassName: "gs-envelope-col-select align-middle",
                cellClassName: "gs-envelope-col-select align-middle",
                render: (_, envelope, index) => (
                    <span data-row-click-ignore="true">
                        <OwnershipSelectCheckbox
                            id={`template-envelope-table-select-${index}`}
                            checked={selection.isSelected(envelope.id)}
                            onCheckedChange={() => selection.toggle(envelope.id)}
                            onClick={(event) => event.stopPropagation()}
                            aria-label={LABELS.TRANSFER_OWNERSHIP}
                        />
                    </span>
                ),
            });
        }

        columns.push(
            {
                key: "subject",
                label: TABLE_HEADERS.SUBJECT,
                width: showTransferActions ? "30%" : "35%",
                sortable: true,
                accessor: (envelope) => envelope.meta_data?.email_subject || "Untitled",
                render: (_, envelope, index) => (
                    <Link
                        to={`/envelope/${envelope.id}`}
                        className="user-card"
                        data-row-click-ignore="true"
                        id={`template-envelope-table-subject-${index}`}
                    >
                        <div className="nk-tnx-type w-100">
                            <div className="bg-fail-dim icon mr-2">
                                <em className="icon ni ni-file icons" id={`template-envelope-table-icon-${index}`} />
                            </div>
                            <div
                                className="data"
                                style={{
                                    marginTop: 4,
                                    maxWidth: "100%",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                <div className="info title-overflow">
                                    <div className="text-dark name">
                                        {envelope.meta_data?.email_subject || "Untitled"}
                                    </div>
                                    <div className="meta" style={{ marginTop: 4 }}>
                                        <span className="version">
                                            <span className="text-soft" id={`template-envelope-table-recipient-name-${index}`}>
                                                {TO_TEXT}: {getRecipientsText(envelope)}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Link>
                ),
            },
            {
                key: "owner",
                label: TABLE_HEADERS.OWNER,
                width: showTransferActions ? "20%" : "25%",
                sortable: true,
                accessor: (envelope) => concatenateName(envelope.owner?.first_name, envelope.owner?.last_name),
                render: (value, _, index) => (
                    <span className="tb-amount" id={`template-envelope-table-owner-${index}`}>
                        {value}
                    </span>
                ),
            },
            {
                key: "status",
                label: TABLE_HEADERS.STATUS,
                width: showTransferActions ? "15%" : "20%",
                accessor: (envelope) => envelope.status,
                render: (_, envelope, index) => (
                    <StatusBadge envelope={envelope} id={`${index}-template-envelope-table`} />
                ),
            },
            {
                key: "created_at",
                label: TABLE_HEADERS.CREATED_AT,
                width: showTransferActions ? "18%" : "20%",
                sortable: true,
                accessor: (envelope) => envelope.created_at,
                render: (_, envelope, index) => (
                    <>
                        <span className="text-dark" id={`template-envelope-table-created-at-date-${index}`}>
                            {DateFormatter(envelope.created_at)}
                        </span>
                        <div className="meta mt-1">
                            <span className="version">
                                <span className="text-soft" id={`template-envelope-table-created-at-time-${index}`}>
                                    {TimeFormatter(envelope.created_at)}
                                </span>
                            </span>
                        </div>
                    </>
                ),
            }
        );

        if (showTransferActions) {
            columns.push({
                key: "actions",
                label: TABLE_HEADERS.ACTIONS,
                width: "9rem",
                headerClassName: "gs-envelope-col-actions",
                cellClassName: "gs-envelope-col-actions",
                render: (_, envelope, index) => (
                    <span data-row-click-ignore="true">
                        <TransferOwnershipButton
                            id={`template-envelope-transfer-ownership-btn-${index}`}
                            iconId={`template-envelope-transfer-ownership-icon-${index}`}
                            onClick={() => handleOpenTransferModal(envelope)}
                        />
                    </span>
                ),
            });
        }

        return columns;
    }, [
        showTransferActions,
        envelopeIds,
        selection.selectedIds,
        selection.areAllSelected,
        selection.areSomeSelected,
        selection.isSelected,
        selection.toggle,
        selection.toggleAll,
    ]);

    useEffect(() => {
        setTemplateLoading(true);
        getTemplateById(
            id as string,
            (response: any) => {
                setTemplateName(response?.name ?? "");
                setTemplateLoading(false);
            },
            () => {
                setTemplateLoading(false);
            }
        );
    }, [id]);

    const prevTemplateIdRef = useRef(id);

    useEffect(() => {
        if (prevTemplateIdRef.current === id) return;
        prevTemplateIdRef.current = id;
        selection.clear();
        setFilter({});
        reset();
    }, [id, reset, selection.clear]);

    const handleApplyFilter = (filterOverride?: Record<string, any>) => {
        const activeFilter = filterOverride ?? filter;

        const hasActiveFilter = Boolean(
            activeFilter.status ||
                activeFilter.createdAtStart ||
                activeFilter.createdAtEnd ||
                activeFilter.owner
        );

        if (!hasActiveFilter) {
            handleReset();
            return;
        }

        handleFilters({
            dateRange: {
                startDate: activeFilter.createdAtStart || null,
                endDate: activeFilter.createdAtEnd || null,
            },
            status: activeFilter.status ?? undefined,
        });
    };

    const handleReset = () => {
        setFilter({});
        reset();
    };

    const showHeaderSkeleton = templateLoading;
    const showTableSkeleton =
        templateLoading || loading || (envelopes.length === 0 && hasNextPage);

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                {showHeaderSkeleton ? (
                    <ListPageHeaderSkeleton showFilter />
                ) : (
                    <TableHead
                        id="template-envelope-table"
                        handleReset={handleReset}
                        pageNo={pagination.page ?? 1}
                        dataLength={pagination.limit ?? PAGE_LENGTH}
                        setFilter={setFilter}
                        filter={filter}
                        setDataLength={() => {}}
                        reset={handleReset}
                        search={handleSearch}
                        title={templateName || HEADINGS.ENVELOPES}
                        showFilter={true}
                        options={StatusOptions}
                        handleApplyFilter={handleApplyFilter}
                        currentEnvo={envelopes.length}
                        totalEnvo={pagination.total ?? 0}
                        searchItem={searchItem}
                        selectedCount={showTransferActions ? selection.selectedIds.length : 0}
                        showMultiselectSelectAll={showTransferActions && isCompactLayout && envelopeIds.length > 0}
                        selectAllChecked={
                            selection.areAllSelected(envelopeIds)
                                ? true
                                : selection.areSomeSelected(envelopeIds)
                                  ? "indeterminate"
                                  : false
                        }
                        onSelectAllChange={() => selection.toggleAll(envelopeIds)}
                        multiselectAction={
                            showTransferActions ? (
                                <TransferOwnershipButton
                                    id="template-envelope-table-bulk-transfer-btn"
                                    iconId="template-envelope-table-bulk-transfer-icon"
                                    className="gs-transfer-ownership-btn--toolbar"
                                    onClick={() => setShowBulkTransferModal(true)}
                                />
                            ) : null
                        }
                    />
                )}
            </div>
            <div>
                {showTableSkeleton ? (
                    isCompactLayout ? (
                        <EnvelopeTableSkeletonMobile showActionColumn={showTransferActions} />
                    ) : (
                        <table
                            className={`nk-tb-list nk-tb-ulist table-weight gs-envelope-table${showTransferActions ? " gs-envelope-table--with-actions" : ""}`}
                            style={{ tableLayout: "fixed", width: "100%" }}
                            aria-hidden
                        >
                            <thead>
                                <tr className="nk-tb-item nk-tb-head">
                                    {showTransferActions ? (
                                        <th className="nk-tb-col gs-envelope-col-select">
                                            <span />
                                        </th>
                                    ) : null}
                                    <th className="nk-tb-col gs-envelope-col-subject">
                                        <span>{TABLE_HEADERS.SUBJECT}</span>
                                    </th>
                                    <th className="nk-tb-col gs-envelope-col-owner">
                                        <span>{TABLE_HEADERS.OWNER}</span>
                                    </th>
                                    <th className="nk-tb-col gs-envelope-col-status">
                                        <span>{TABLE_HEADERS.STATUS}</span>
                                    </th>
                                    <th className="nk-tb-col gs-envelope-col-created">
                                        <span>{TABLE_HEADERS.CREATED_AT}</span>
                                    </th>
                                    {showTransferActions ? (
                                        <th className="nk-tb-col gs-envelope-col-actions">
                                            <span>{TABLE_HEADERS.ACTIONS}</span>
                                        </th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody>
                                <EnvelopeTableSkeletonDesktop showActionColumn={showTransferActions} />
                            </tbody>
                        </table>
                    )
                ) : (
                    <>
                        <DataTable
                            type="template_envelopes"
                            data={envelopes}
                            columns={envelopeColumns}
                            loading={false}
                            loadingMore={loadingMore}
                            hideHeaderTopBorder
                            tableClassName={`gs-envelope-table${showTransferActions ? " gs-envelope-table--with-actions" : ""}`}
                            getRowClassName={(envelope) =>
                                selection.isSelected(envelope.id) ? "gs-table-row-selected" : undefined
                            }
                            onRowClick={(envelope) => navigate(`/envelope/${envelope.id}`)}
                        />
                        <div ref={observerRef} style={{ height: 1 }} />
                    </>
                )}
            </div>
            {selectedEnvelope ? (
                <TransferOwnershipModal
                    show={showTransferModal}
                    onHide={handleCloseTransferModal}
                    onTransferOwnership={handleTransferOwnership}
                    loading={transferLoading}
                    currentOwnerId={selectedEnvelope?.owner?.id}
                    title={LABELS.TRANSFER_ENVELOPE_OWNERSHIP}
                    description={LABELS.TRANSFER_ENVELOPE_OWNERSHIP_DESCRIPTION}
                />
            ) : null}
            {showBulkTransferModal ? (
                <TransferOwnershipModal
                    show={showBulkTransferModal}
                    onHide={() => setShowBulkTransferModal(false)}
                    onTransferOwnership={handleBulkTransferOwnership}
                    loading={bulkTransferLoading}
                    title={LABELS.TRANSFER_ENVELOPE_OWNERSHIP}
                    description={LABELS.BULK_TRANSFER_ENVELOPE_OWNERSHIP_DESCRIPTION}
                />
            ) : null}
        </div>
    );
};

export default TemplateEnvelopeList;
